import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStorageStore, UNSORTED_SEQUENCES_ID, NAME_COLLISION } from '../../store/useStorageStore';
import { useDanceStore, getFriendlyErrorMessage } from '../../store/useDanceStore';
import { ExplorerView } from './ExplorerView';
import { FolderTree } from './FolderTree';
import { Play, Clock, Trash2, Music, AlertCircle, Edit2, Copy, Scissors, Clipboard, Film, FolderPlus, FilePlus, Home, Share2, ArrowUpRight, ArrowDownLeft, X, Sparkles, Users, Star } from 'lucide-react';
import { calculateFileHash } from '../../utils/audioAnalyzer';
import { trackShareEvent } from '../../lib/analytics';
import { CollaboratorsPopover } from '../CollaboratorsPopover';

import { DanceState, SavedSequence } from '../../types';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAuthStore } from '../../store/useAuthStore';


export const SequencesModule: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useIsMobile();
  const user = useAuthStore(state => state.user);
  const tier = useAuthStore(state => state.tier);
  const allFolders = useStorageStore(state => state.folders);
  const folders = React.useMemo(() => allFolders.filter(f => f.category === 'sequences'), [allFolders]);
  const allSequences = useStorageStore(state => state.sequences);
  
  const createFolder = useStorageStore(state => state.createFolder);
  const renameFolder = useStorageStore(state => state.renameFolder);
  const deleteFolder = useStorageStore(state => state.deleteFolder);
  const deleteSequence = useStorageStore(state => state.deleteSequence);
  const moveSequence = useStorageStore(state => state.moveSequence);
  const renameSequence = useStorageStore(state => state.renameSequence);
  const duplicateSequence = useStorageStore(state => state.duplicateSequence);
  const saveSequence = useStorageStore(state => state.saveSequence);
  const multiMoveSequences = useStorageStore(state => state.multiMoveSequences);
  const multiDeleteSequences = useStorageStore(state => state.multiDeleteSequences);
  const clipboard = useStorageStore(state => state.clipboard);
  const setClipboard = useStorageStore(state => state.setClipboard);
  const unseenUpdatedSequenceIds = useStorageStore(state => state.unseenUpdatedSequenceIds);
  const markSequenceAsSeen = useStorageStore(state => state.markSequenceAsSeen);
  
  const loadSequence = useDanceStore((state: DanceState) => state.loadSequence);
  const confirmAction = useDanceStore((state: DanceState) => state.confirmAction);
  const closeConfirm = useDanceStore((state: DanceState) => state.closeConfirm);
  const openInputDialog = useDanceStore((state: DanceState) => state.openInputDialog);
  const setActiveModule = useDanceStore((state: DanceState) => state.setActiveModule);
  const newSequence = useDanceStore((state: DanceState) => state.newSequence);
  const openSoundtrackModalForSequence = useDanceStore((state: DanceState) => state.openSoundtrackModalForSequence);
  
  const loadMusic = useDanceStore((state: DanceState) => state.loadMusic);
  const currentAudioHash = useDanceStore((state: DanceState) => state.audioHash);
  const hasMusicLoaded = useDanceStore((state: DanceState) => state.hasMusicLoaded);
  const hasUnsavedChanges = useDanceStore((state: DanceState) => state.hasUnsavedChanges);
  const hasUnsavedSequenceChanges = useDanceStore((state: DanceState) => state.hasUnsavedSequenceChanges);
  const keyframes = useDanceStore((state: DanceState) => state.keyframes);
  const loadedSequence = useDanceStore((state: DanceState) => state.loadedSequence);
  const librarySelection = useDanceStore(state => state.librarySelection);
  const setLibrarySelection = useDanceStore(state => state.setLibrarySelection);
  const toggleLibrarySelection = useDanceStore(state => state.toggleLibrarySelection);

  const selectedFolderId = useStorageStore(state => state.selectedSequenceFolderId);
  const setSelectedFolderId = useStorageStore(state => state.setSelectedSequenceFolderId);

  const handleCreateFolder = (parentId: string | null) => {
    openInputDialog({
      title: parentId ? 'New Subfolder' : 'New Root Folder',
      defaultName: 'New Folder',
      placeholder: t('library.enter_folder_name'),
      submitLabel: t('library.create'),
      onSave: (name) => {
        let id: string;
        try {
          id = createFolder(name, 'sequences', parentId);
        } catch (err: any) {
          if (err.message === NAME_COLLISION) {
            throw new Error(t('errors.folder_name_collision', 'A folder with that name already exists here.'));
          }
          throw err;
        }
        if (id) setSelectedFolderId(id);
      }
    });
  };

  // Inventory row 33 — changing the active folder clears the current card
  // selection. Skip the initial mount so a selection set before the first
  // render (e.g. restoring state) isn't immediately wiped out.
  const previousFolderIdRef = useRef(selectedFolderId);
  useEffect(() => {
    if (previousFolderIdRef.current !== selectedFolderId) {
      previousFolderIdRef.current = selectedFolderId;
      setLibrarySelection([]);
    }
  }, [selectedFolderId]);

  const fetchAndLoadMusic = useCallback(async (seq: SavedSequence) => {
    useDanceStore.setState({ isAnalyzing: true });
    confirmAction({
      title: t('music.loading_sequence_title', { defaultValue: 'Loading Sequence' }),
      message: t('music.loading_sequence_message', { defaultValue: 'Downloading and validating soundtrack...' }),
      isLoading: true,
      isNonDismissible: true,
      onConfirm: () => {},
    });
    try {
      const downloadAndValidateMusic = useDanceStore.getState().downloadAndValidateMusic;
      const file = await downloadAndValidateMusic(seq.audioSourceUrl!, seq.audioHash, seq.audioFileName);

      useDanceStore.setState({ isAnalyzing: false });
      await loadMusic(file, false, 'url', seq.audioSourceUrl!);
      loadSequence(seq);
      setActiveModule('PoseDesigner');
      closeConfirm();
    } catch (err: any) {
      console.error('Failed to fetch/validate music for sequence:', err);
      const errorMsg = getFriendlyErrorMessage(err, t);

      closeConfirm();
      useDanceStore.setState({ isAnalyzing: false });
      openSoundtrackModalForSequence({ sequence: seq, initialUrl: seq.audioSourceUrl!, initialError: errorMsg });
    }
  }, [loadMusic, loadSequence, setActiveModule, confirmAction, closeConfirm, openSoundtrackModalForSequence, t]);

  const handleLoadSequence = (seq: SavedSequence) => {
    markSequenceAsSeen(seq);
    const isCurrentEmpty = !loadedSequence && keyframes.length === 0 && !hasMusicLoaded;

    // 1. If no music is required, load immediately
    if (!seq.audioFileName) {
      if (isCurrentEmpty) {
        loadSequence(seq);
        setActiveModule('PoseDesigner');
        return;
      }
      const hasChanges = hasUnsavedChanges || hasUnsavedSequenceChanges;
      confirmAction({
        title: hasChanges ? t('timeline.unsaved_changes_title') : t('library.load_sequence_title'),
        message: hasChanges 
          ? t('timeline.unsaved_changes_message')
          : t('library.load_sequence_confirm'),
        onConfirm: () => {
          loadSequence(seq);
          setActiveModule('PoseDesigner');
        }
      });
      return;
    }

    // 2. Check if the correct music is ALREADY loaded
    const isMusicMatch = hasMusicLoaded && seq.audioHash && (seq.audioHash === currentAudioHash);

    if (isMusicMatch) {
      if (isCurrentEmpty) {
        loadSequence(seq);
        setActiveModule('PoseDesigner');
        return;
      }
      const hasChanges = hasUnsavedChanges || hasUnsavedSequenceChanges;
      confirmAction({
        title: hasChanges ? t('timeline.unsaved_changes_title') : t('library.load_sequence_title'),
        message: hasChanges
          ? t('timeline.unsaved_changes_message')
          : t('library.load_sequence_confirm'),
        onConfirm: () => {
          loadSequence(seq);
          setActiveModule('PoseDesigner');
        }
      });
      return;
    }

    // 3. Music required but not loaded/matching — auto-fetch if URL is saved
    if (seq.audioSourceUrl) {
      const doLoad = () => fetchAndLoadMusic(seq);
      if (isCurrentEmpty && !hasUnsavedChanges && !hasUnsavedSequenceChanges) {
        doLoad();
      } else {
        const hasChanges = hasUnsavedChanges || hasUnsavedSequenceChanges;
        confirmAction({
          title: hasChanges ? t('timeline.unsaved_changes_title') : t('library.load_sequence_title'),
          message: hasChanges ? t('timeline.unsaved_changes_message') : t('library.load_sequence_confirm'),
          onConfirm: doLoad,
        });
      }
      return;
    }

    // 4. No saved URL — was a local file. Open SoundtrackModal with local-file context.
    openSoundtrackModalForSequence({ sequence: seq, isLocalFile: true });
  };

  const currentFolder = folders.find(f => f.id === selectedFolderId);
  const sequencesInFolder = selectedFolderId === 'shared-sequences'
    ? allSequences.filter(s => {
        const isIncoming = (s.userId && s.userId !== user?.uid) || (user?.email && s.collaborators?.includes(user.email));
        const isOutgoing = (s.userId === user?.uid || (!s.userId && user?.email && !s.collaborators?.includes(user.email))) && s.collaborators && s.collaborators.length > 0;
        return isIncoming || isOutgoing;
      })
    : allSequences.filter(s => s.folderId === selectedFolderId);
  const currentSequenceIds = React.useMemo(() => sequencesInFolder.map(s => s.id), [sequencesInFolder]);

  const handleDeleteSelected = () => {
    const selection = useDanceStore.getState().librarySelection;
    if (selection.length === 0) return;

    // Check if all selected are Incoming Shares (so it's an Opt Out)
    const selectedSeqs = allSequences.filter(s => selection.includes(s.id));
    const allIncoming = selectedSeqs.length > 0 && selectedSeqs.every(s => (s.userId && s.userId !== user?.uid) || (user?.email && s.collaborators?.includes(user.email)));

    const title = allIncoming 
      ? t('sharing.opt_out_title', 'Opt Out') 
      : t('library.delete_selected_sequences');
    const message = allIncoming
      ? t('sharing.opt_out_message_count', 'Are you sure you want to opt out of collaborating on the selected sequence(s)?', { count: selection.length })
      : t('library.delete_selected_message', { count: selection.length });

    confirmAction({
      title,
      message,
      onConfirm: () => {
        multiDeleteSequences(selection);
        setLibrarySelection([]);
      }
    });
  };

  const handleNewSequence = () => {
    if (hasUnsavedSequenceChanges || hasUnsavedChanges) {
      confirmAction({
        title: t('folderTree.new_sequence_title'),
        message: t('folderTree.unsaved_changes'),
        onConfirm: () => {
          newSequence();
          setActiveModule('PoseDesigner');
        },
        confirmLabel: t('folderTree.discard_and_new'),
        cancelLabel: t('common.cancel')
      });
    } else if (loadedSequence) {
      confirmAction({
        title: t('library.new_sequence_loaded_title'),
        message: t('library.new_sequence_loaded_message'),
        onConfirm: () => {
          newSequence();
          setActiveModule('PoseDesigner');
        },
        confirmLabel: t('library.new_sequence_continue'),
        cancelLabel: t('common.cancel')
      });
    } else {
      newSequence();
      setActiveModule('PoseDesigner');
    }
  };

  const handleCopySelected = () => {
    const selection = useDanceStore.getState().librarySelection;
    if (selection.length === 0) return;
    setClipboard({ type: 'copy', itemType: 'sequence', itemIds: [...selection] });
    useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: selection.length }));
  };

  const handleCutSelected = () => {
    const selection = useDanceStore.getState().librarySelection;
    if (selection.length === 0) return;
    setClipboard({ type: 'cut', itemType: 'sequence', itemIds: [...selection] });
    useDanceStore.getState().setToastMessage(t('library.cut_to_clipboard', { count: selection.length }));
  };

  const handlePaste = () => {
    if (!clipboard || clipboard.itemType !== 'sequence') return;
    
    if (clipboard.type === 'cut') {
      multiMoveSequences(clipboard.itemIds, selectedFolderId);
      setClipboard(null);
      useDanceStore.getState().setToastMessage(t('library.moved_items', { count: clipboard.itemIds.length }));
    } else {
      clipboard.itemIds.forEach(id => {
        duplicateSequence(id, selectedFolderId);
      });
      useDanceStore.getState().setToastMessage(t('library.pasted_items', { count: clipboard.itemIds.length }));
    }
  };

  useEffect(() => {
    if (isMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.key) return;
      if (useDanceStore.getState().activeModule !== 'Sequences') return;
      if (document.activeElement?.tagName === 'INPUT') return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        handleDeleteSelected();
      } else if (e.key === 'Escape') {
        setLibrarySelection([]);
      } else if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
        handleCopySelected();
      } else if (e.key === 'x' && (e.ctrlKey || e.metaKey)) {
        handleCutSelected();
      } else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
        handlePaste();
      } else if (e.key === 'd' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        librarySelection.forEach(id => duplicateSequence(id));
      } else if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setLibrarySelection(currentSequenceIds);
      } else if (e.key === 'F2' && librarySelection.length === 1) {
        const seq = allSequences.find(s => s.id === librarySelection[0]);
        if (seq) {
          openInputDialog({
            title: t('library.rename'),
            defaultName: seq.name,
            onSave: (newName) => {
              try {
                renameSequence(seq.id, newName);
              } catch (err: any) {
                if (err.message === NAME_COLLISION) {
                  throw new Error(t('errors.sequence_name_collision', 'A sequence with that name already exists in this folder.'));
                }
                throw err;
              }
            }
          });
        }
      } else if (e.key === 'l' && librarySelection.length === 1) {
        const seq = allSequences.find(s => s.id === librarySelection[0]);
        if (seq) {
          handleLoadSequence(seq);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [librarySelection, selectedFolderId, clipboard, currentSequenceIds, isMobile]);

  const folderTitle = selectedFolderId === null ? t('folderTree.root', { defaultValue: 'Root' }) : (currentFolder ? currentFolder.name : t('library.select_folder'));

  const sidebar = (
    <FolderTree 
      folders={folders}
      selectedFolderId={selectedFolderId}
      onSelectFolder={setSelectedFolderId}
      onCreateFolder={handleCreateFolder}
      onRenameFolder={renameFolder}
      onDeleteFolder={deleteFolder}
      onMoveItem={moveSequence}
      title={t('library.sequences')}
    />
  );

  const content = (
    <div
      className="sequence-list-drop-zone"
      onClick={() => setLibrarySelection([])}
      onDragOver={(e) => {
        if (isMobile) return;
        const hasSource = e.dataTransfer.types.some(t => t.toLowerCase() === 'sourcetype');
        if (hasSource) {
          e.preventDefault();
          if (e.ctrlKey || e.metaKey) {
            e.dataTransfer.dropEffect = 'copy';
          } else {
            e.dataTransfer.dropEffect = 'none';
          }
        }
      }}
      onDrop={(e) => {
        if (isMobile) return;
        e.preventDefault();
        const sourceIdsStr = e.dataTransfer.getData('sourceId');
        const sourceType = e.dataTransfer.getData('sourceType');
        if (sourceIdsStr && sourceType === 'sequence') {
          const ids = sourceIdsStr.split(',');
          const isCopy = e.ctrlKey || e.metaKey;
          if (isCopy) {
            ids.forEach(id => {
              duplicateSequence(id, selectedFolderId);
            });
            useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: ids.length }));
          }
        }
      }}
    >
      {selectedFolderId && sequencesInFolder.length === 0 && (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', fontSize: '0.875rem', padding: '8px 0' }}>
          {t('library.no_sequences')}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {sequencesInFolder.map(seq => (
          <div 
            key={seq.id} 
            draggable={!isMobile} 
            className={`sequence-card-compact ${librarySelection.includes(seq.id) ? 'selected' : ''}`}
            style={{ position: 'relative' }}
            onDragStart={(e) => {
              if (isMobile) return;
              const dragIds = librarySelection.includes(seq.id) ? librarySelection : [seq.id];
              e.dataTransfer.setData('sourceId', dragIds.join(','));
              e.dataTransfer.setData('sourceType', 'sequence');
              e.dataTransfer.setData('sourceFolderId', selectedFolderId || '');
              if (selectedFolderId === 'shared-sequences') {
                e.dataTransfer.setData('choreographer/from-shared', 'true');
              }
              e.dataTransfer.effectAllowed = 'copyMove';
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (isMobile) {
                  handleLoadSequence(seq);
                } else {
                  toggleLibrarySelection(seq.id, e.ctrlKey || e.metaKey, e.shiftKey, currentSequenceIds);
                }
            }}
            onDoubleClick={(e) => {
                e.stopPropagation();
                if (!isMobile) {
                  handleLoadSequence(seq);
                }
            }}
            onContextMenu={(e) => {
              if (isMobile) return;
              e.preventDefault();
              // Ensure the item is selected if it wasn't
              let targetSelection = librarySelection;
              if (!librarySelection.includes(seq.id)) {
                targetSelection = [seq.id];
                setLibrarySelection(targetSelection);
              }

              const openContextMenu = useDanceStore.getState().openContextMenu;
              const hasSharedSelected = allSequences.filter(s => targetSelection.includes(s.id)).some(s => (s.userId && s.userId !== user?.uid) || (user?.email && s.collaborators?.includes(user.email)));
              const isIncoming = !!((seq.userId && seq.userId !== user?.uid) || (user?.email && seq.collaborators?.includes(user.email)));
              
              openContextMenu(e.clientX, e.clientY, [
                {
                  label: t('library.load_sequence'),
                  icon: Play,
                  disabled: targetSelection.length > 1,
                  action: () => handleLoadSequence(seq)
                },
                {
                  label: seq.collaborators && seq.collaborators.length > 0 
                    ? t('sharing.menu_shared', 'Shared ({count})', { count: seq.collaborators.length })
                    : t('sharing.menu_share', 'Share...'),
                  icon: Share2,
                  disabled: targetSelection.length > 1 || isIncoming,
                  className: seq.collaborators && seq.collaborators.length > 0 ? 'share-active-menu' : undefined,
                  action: () => {
                    if (tier === 'registered' || tier === 'anonymous') {
                      confirmAction({
                        title: t('sharing.upgrade_title', 'Upgrade Required'),
                        message: t('sharing.upgrade_message', 'Only Basic and Pro subscribers can share sequences with others. Please upgrade your subscription to enable sharing.'),
                        confirmLabel: t('profile.upgrade', 'Upgrade'),
                        onConfirm: () => setActiveModule('Profile')
                      });
                    } else {
                      useDanceStore.getState().openShareDialog(seq);
                    }
                  }
                },
                ...(seq.collaborators && seq.collaborators.length > 0 && !isIncoming ? [
                  {
                    label: t('sharing.menu_unshare', 'Unshare'),
                    icon: X,
                    disabled: targetSelection.length > 1,
                    action: () => {
                      confirmAction({
                        title: t('sharing.unshare_title', 'Unshare Sequence'),
                        message: t('sharing.unshare_message', 'Are you sure you want to unshare this sequence? All collaborators will lose access to it.'),
                        onConfirm: () => {
                          try {
                            saveSequence({
                              ...seq,
                              collaborators: []
                            });
                            trackShareEvent('unshared', { sequence_id: seq.id });
                          } catch (err: any) {
                            console.error('Failed to unshare sequence:', err);
                          }
                        }
                      });
                    }
                  }
                ] : []),
                { isDivider: true },
                {
                  label: t('library.rename'),
                  icon: Edit2,
                  shortcut: 'F2',
                  disabled: targetSelection.length > 1 || hasSharedSelected,
                  action: () => {
                    openInputDialog({
                      title: t('library.rename'),
                      defaultName: seq.name,
                      onSave: (newName) => {
                        try {
                          renameSequence(seq.id, newName);
                        } catch (err: any) {
                          if (err.message === NAME_COLLISION) {
                            throw new Error(t('errors.sequence_name_collision', 'A sequence with that name already exists in this folder.'));
                          }
                          throw err;
                        }
                      }
                    });
                  }
                },
                {
                  label: t('library.duplicate'),
                  icon: Copy,
                  shortcut: 'Ctrl+D',
                  disabled: hasSharedSelected,
                  action: () => {
                    targetSelection.forEach(id => duplicateSequence(id));
                  }
                },
                { isDivider: true },
                {
                  label: t('library.cut'),
                  icon: Scissors,
                  shortcut: 'Ctrl+X',
                  disabled: hasSharedSelected,
                  action: handleCutSelected
                },
                {
                  label: t('library.copy'),
                  icon: Copy,
                  shortcut: 'Ctrl+C',
                  action: handleCopySelected
                },
                {
                  label: t('library.paste'),
                  icon: Clipboard,
                  shortcut: 'Ctrl+V',
                  disabled: !clipboard || clipboard.itemType !== 'sequence' || selectedFolderId === 'shared-sequences',
                  action: handlePaste
                },
                { isDivider: true },
                { 
                  label: isIncoming 
                    ? t('sharing.menu_opt_out', 'Opt Out')
                    : targetSelection.length > 1 
                      ? t('library.delete_item', { count: targetSelection.length }) 
                      : t('library.delete_item'), 
                  icon: Trash2, 
                  className: 'danger',
                  shortcut: 'Del',
                  action: handleDeleteSelected
                }
              ]);
            }}
          >
            {unseenUpdatedSequenceIds.includes(seq.id) && (
              <span className="sequence-updated-star" title={t('sharing.sequence_updated_by_author', 'Updated by author')}>
                <Star size={14} fill="#38bdf8" color="#38bdf8" />
              </span>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 6, paddingRight: unseenUpdatedSequenceIds.includes(seq.id) ? 18 : 0 }}>
              <h4 className="sequence-card-compact-title" title={seq.name} style={{ margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seq.name}</h4>
            </div>
            <div className="sequence-card-compact-stats">
              <span className="sequence-card-compact-stat-item">
                <Clock size={14} /> {seq.duration}s
              </span>
              {seq.audioFileName && (
                <>
                  <div className="sequence-card-stat-divider" />
                  <span title={`${t('library.requires_audio', 'Requires')}: ${seq.audioFileName}`} className="sequence-card-compact-stat-item audio">
                    <Music size={14} />
                  </span>
                </>
              )}
            </div>
            {((seq.userId && seq.userId !== user?.uid) || (user?.email && seq.collaborators?.includes(user.email))) && (
              <CollaboratorsPopover
                mode="incoming"
                ownerName={seq.ownerName || (seq.userId !== user?.uid ? seq.userId : null) || t('sharing.someone', 'Someone')}
              >
                <div
                  className="sequence-card-compact-sharing incoming"
                  title={`${t('sharing.incoming_share', 'Shared with you')}: ${seq.ownerName || (seq.userId !== user?.uid ? seq.userId : null) || t('sharing.someone', 'Someone')}`}
                >
                  <ArrowDownLeft size={13} style={{ flexShrink: 0 }} />
                  <span className="sequence-card-sharer-name">
                    ({seq.ownerName || (seq.userId !== user?.uid ? seq.userId : null) || t('sharing.someone', 'Someone')})
                  </span>
                </div>
              </CollaboratorsPopover>
            )}
            {(seq.userId === user?.uid || (!seq.userId && user?.email && !seq.collaborators?.includes(user.email))) && seq.collaborators && seq.collaborators.length > 0 && (
              <CollaboratorsPopover mode="outgoing" emails={seq.collaborators}>
                <div
                  className="sequence-card-compact-sharing outgoing"
                  title={t('sharing.shared_with_count', { count: seq.collaborators.length })}
                >
                  <Users size={13} style={{ flexShrink: 0 }} />
                  <span>({seq.collaborators.length})</span>
                </div>
              </CollaboratorsPopover>
            )}
            <div className="sequence-card-compact-dates">
              <span>{t('library.date_created', { date: new Date(seq.dateCreated).toLocaleDateString(i18n.language) })}</span>
              <span>{t('library.date_modified', { date: new Date(seq.dateModified).toLocaleDateString(i18n.language) })}</span>
            </div>

            {!isMobile && (
              <div className="folder-actions" style={{ gap: 4 }} onClick={(e) => e.stopPropagation()}>
                <button 
                  onClick={() => handleLoadSequence(seq)}
                  style={{ background: 'transparent', padding: 2, border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
                  title={t('library.load')}
                >
                  <Play size={14} fill="currentColor" />
                </button>
                <button 
                  onClick={() => {
                    const isIncoming = !!((seq.userId && seq.userId !== user?.uid) || (user?.email && seq.collaborators?.includes(user.email)));
                    confirmAction({
                      title: isIncoming ? t('sharing.opt_out_title', 'Opt Out') : t('library.delete_item'),
                      message: isIncoming 
                        ? t('sharing.opt_out_message', 'Are you sure you want to opt out of collaborating on this sequence? You will lose access and it will be removed from your Shared folder.')
                        : t('library.delete_selected_message', { count: 1 }),
                      onConfirm: () => deleteSequence(seq.id)
                    });
                  }}
                  style={{ background: 'transparent', padding: 2, border: 'none', color: '#fca5a5', cursor: 'pointer' }}
                  title={((seq.userId && seq.userId !== user?.uid) || (user?.email && seq.collaborators?.includes(user.email))) ? t('sharing.menu_opt_out', 'Opt Out') : t('library.delete_item')}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const titleNode = (
    <div className="explorer-header-title-container">
      <div className="module-icon-box" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Film size={24} />
      </div>
      <div>
        <h2 style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.02em', color: '#38bdf8' }}>
          {t('library.sequences', { defaultValue: 'Sequences' })}
        </h2>
      </div>
    </div>
  );

  const breadcrumbPath: { id: string; name: string; parentId: string | null }[] = [];
  if (selectedFolderId !== null) {
    let curr: string | null = selectedFolderId;
    while (curr) {
      const f = folders.find(x => x.id === curr);
      if (!f) break;
      breadcrumbPath.unshift(f);
      curr = f.parentId;
    }
  }

  const contentHeader = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, minHeight: '24px', width: '100%' }}>
      {/* Breadcrumb — left side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flex: 1, minWidth: 0, overflowX: 'auto', scrollbarWidth: 'none' as const }}>
        <button
          onClick={() => setSelectedFolderId(UNSORTED_SEQUENCES_ID)}
          title={t('folderTree.root', { defaultValue: 'Root' })}
          style={{ background: 'transparent', border: 'none', color: selectedFolderId === UNSORTED_SEQUENCES_ID ? '#38bdf8' : 'rgba(255,255,255,0.45)', padding: '2px 4px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
        >
          <Home size={13} />
        </button>
        {breadcrumbPath.map((f, i) => (
          <React.Fragment key={f.id}>
            <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem', flexShrink: 0 }}>/</span>
            <button
              onClick={() => setSelectedFolderId(f.id)}
              style={{ background: 'transparent', border: 'none', color: i === breadcrumbPath.length - 1 ? '#38bdf8' : 'rgba(255,255,255,0.45)', fontSize: '0.75rem', fontWeight: i === breadcrumbPath.length - 1 ? 700 : 500, padding: '2px 4px', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {f.name}
            </button>
          </React.Fragment>
        ))}
      </div>
      {/* Action buttons — right side */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, paddingLeft: 16 }}>
        <button
          id="library-new-root-folder-btn"
          onClick={() => handleCreateFolder(null)}
          className="timeline-btn blue"
          style={{ display: 'flex', alignItems: 'center', height: '24px' }}
        >
          <FolderPlus size={14}/>
          <span>{t('folderTree.new_root_folder', { defaultValue: 'New Folder' })}</span>
        </button>
        <button
          onClick={handleNewSequence}
          className="timeline-btn blue"
          style={{ display: 'flex', alignItems: 'center', height: '24px' }}
        >
          <FilePlus size={14}/>
          <span>{t('folderTree.new_sequence', { defaultValue: 'New Sequence' })}</span>
        </button>
      </div>
    </div>
  );

  const isSidebarExpanded = isMobile && (selectedFolderId === null);
  return (
    <>
      <ExplorerView title={titleNode} sidebar={sidebar} content={content} contentHeader={contentHeader} isSidebarExpanded={isSidebarExpanded} />
    </>
  );
};
